import React, {useCallback, useEffect, ReactNode} from "react";
import {toFormikValidationSchema} from "zod-formik-adapter";
import {PccFacilityMappingModel} from "../../../../../utils/types/facility";
import {Subtitle, Card, BlockField, QueryVariableMap} from "@reside/ui";
import {Formik} from "formik";
import {z} from "zod";

import {PccMappingFormContext} from "./PccMappingFormContext";
import {INITIAL_VALUES_FOR_PCC_DISABLED_FACILITY} from "./constants";
import {BackdropSpinner} from "../../../../../atoms/spinner";
import {FacilityPccMappingFormValues} from "./types";
import {PccFeatureFlags} from "./PccFeatureFlags";
import {hasPccMappingData} from "./helpers";
import {FormButtons} from "./FormButtons";
import {FormFields} from "./FormFields";
import {
  usePccFacilityMappingQuery,
  usePccFacilityMappingMutation,
  useFacilityPccFeatureFlags,
  useFacilitySettingsParams,
} from "../../../hooks";
import {useTemplateVersionQuery} from "../../../../../hooks/useTemplateVersionQuery";

import {
  FacilitySettingsSection,
  FacilitySettingsScrollTarget,
} from "../FacilitySettingsSection";
import {SECTION_PCC, SECTION_PCC_INTEGRATION} from "../FacilitySettingsSideNav";

/**
 * FacilityPccMappingForm component handles the form logic and UI for managing
 * PointClickCare (PCC) facility connection settings.
 *
 * Renders workflow sections via `children` after the PCC Integration section,
 * still inside the same Formik context for document category fields.
 */
export const FacilityPccMappingForm = ({
  isPccEnabled,
  children,
  pccTemplateBlocks = [],
  queryVariables,
}: {
  isPccEnabled: boolean;
  children?: ReactNode;
  pccTemplateBlocks?: ReadonlyArray<any>;
  queryVariables?: QueryVariableMap;
}) => {
  const {facilityId} = useFacilitySettingsParams(); // Get facility ID from route parameters

  const {activeFeatureFlagsOfFacility} = useFacilityPccFeatureFlags();

  // Fetch template workflow types to render per-workflow PCC category selectors
  const {data: templateVersionData} = useTemplateVersionQuery({facilityId});
  const allWorkflowTypes: string[] = (
    (templateVersionData?.version as {workflowTypes?: string[]} | undefined)
      ?.workflowTypes ?? []
  );
  // When the template defines an "arbitration" workflow it is handled via workflowCategoryMappings.
  const workflowTypes: string[] = allWorkflowTypes.filter(
    wf => wf.toLowerCase() !== "admission",
  );

  // State to hold the initial form values until the data is fetched
  const [initialValues, setInitialValues] = React.useState({
    ...INITIAL_VALUES_FOR_PCC_DISABLED_FACILITY,
    pccFeatureFlags: [],
  });

  // Fetch current PCC facility mapping data
  const {
    data: pccFacilityMapping,
    isFetching: isFetchingPccFacilityMapping,
    refetch: refetchPccFacilityMapping,
  } = usePccFacilityMappingQuery({
    facilityId,
  });

  // Mutation hook for updating the PCC facility mapping
  const {
    mutate: updatePccFacilityMapping,
    isLoading: isUpdatingPccFacilityMapping,
    isSuccess: isUpdatedPccFacilityMapping,
    isError: isErrorUpdatingPccFacilityMapping,
  } = usePccFacilityMappingMutation();

  // Handle form submission
  const onFormikSubmit = useCallback(
    (pccFacilityMapping: PccFacilityMappingModel) =>
      updatePccFacilityMapping({facilityId, pccFacilityMapping}),
    [updatePccFacilityMapping, facilityId],
  );

  // Re-set initial form values when data is fetched to avoid stale data
  useEffect(() => {
    // Build workflowCategoryMappings from fetched data, keyed by each workflow type
    const workflowCategoryMappings = Object.fromEntries(
      workflowTypes.map(wf => [
        wf,
        pccFacilityMapping?.workflowCategoryMappings?.[wf] ?? null,
      ]),
    );

    const newValues = hasPccMappingData(pccFacilityMapping)
      ? {
          ...pccFacilityMapping,
          pccFacilityId: pccFacilityMapping.pccFacilityId.toString(),
          pccFeatureFlags: activeFeatureFlagsOfFacility.filter(featureFlag =>
            featureFlag.includes("PCC"),
          ) as string[],
          workflowCategoryMappings,
        } // Set initial values to value of facility mapping data
      : {
          pccFacilityId: "" as unknown as number,
          pccOrganizationId: "",
          pccFeatureFlags: [] as string[],
          workflowCategoryMappings,
        }; // Re-set values to empty when mapping is deleted so that button is disabled again

    // Only update state if values have changed
    if (JSON.stringify(newValues) !== JSON.stringify(initialValues)) {
      setInitialValues(newValues as FacilityPccMappingFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pccFacilityMapping, activeFeatureFlagsOfFacility, initialValues, JSON.stringify(workflowTypes)]);

  // Refetch data if the update is successful
  useEffect(() => {
    if (isUpdatedPccFacilityMapping) {
      refetchPccFacilityMapping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatedPccFacilityMapping]);

  // Create a schema for the form validationsta i
  const Schema = z
    .object({
      pccOrganizationId: z.string().optional(),
      pccFacilityId: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const bothFilled = data.pccOrganizationId && data.pccFacilityId; // Both fields filled
      const bothEmpty = !data.pccOrganizationId && !data.pccFacilityId; // Both fields empty

      if (!bothFilled && !bothEmpty) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Both fields must be filled or both must be empty.",
          path: ["pccOrganizationId"], // Add error to `pccOrganizationId`
        });

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Both fields must be filled or both must be empty.",
          path: ["pccFacilityId"], // Add error to `pccFacilityId`
        });
      }
    });

  return (
    <FacilitySettingsSection id={SECTION_PCC_INTEGRATION}>
      {pccTemplateBlocks.map(block => (
        <FacilitySettingsScrollTarget
          key={String(block.id)}
          id={String(block.id)}
        >
          <BlockField block={block} queryVariables={queryVariables} />
        </FacilitySettingsScrollTarget>
      ))}

      <Formik<FacilityPccMappingFormValues>
        enableReinitialize
        initialValues={initialValues}
        onSubmit={values => onFormikSubmit(values)}
        validationSchema={toFormikValidationSchema(Schema)}
      >
        {({isValid}) => (
          <PccMappingFormContext.Provider
            value={{isUpdating: isUpdatingPccFacilityMapping}}
          >
            <BackdropSpinner active={isFetchingPccFacilityMapping}>
              <FacilitySettingsScrollTarget id={SECTION_PCC}>
                <Card>
                  <Subtitle style={{marginBottom: "20px"}}>
                    PointClickCare Connection Settings
                  </Subtitle>

                  <FormFields
                    isPccEnabled={isPccEnabled}
                    isErrorUpdatingPccFacilityMapping={
                      isErrorUpdatingPccFacilityMapping
                    }
                  />

                  <FormButtons
                    isFormValid={isValid}
                    isPccFacilityMappingUpdating={isUpdatingPccFacilityMapping}
                  />

                  <PccFeatureFlags
                    isPccEnabled={isPccEnabled}
                    facilityId={facilityId}
                    hasPccMappingData={Boolean(
                      pccFacilityMapping?.pccOrganizationId,
                    )}
                    isPccFacilityMappingUpdating={isUpdatingPccFacilityMapping}
                  />
                </Card>
              </FacilitySettingsScrollTarget>
            </BackdropSpinner>

            {children}
          </PccMappingFormContext.Provider>
        )}
      </Formik>
    </FacilitySettingsSection>
  );
};
